import {registerBrick, Auth, CloudObject, User} from 'olympe';
import {ReactBrick, useProperty, parseCurrentHash} from "@olympeio/core";
import {combineLatest} from "rxjs";
import React, { useState, useEffect } from 'react';
import { Heart, Home, Calendar, Users, Menu, FileText, Bell, Search, X, CalendarRange, TvMinimalPlay, MessageCircleMore, UserRoundCog, LifeBuoy, Earth, HeartHandshake, ThumbsUp, Lock, History } from 'lucide-react';
import {Box} from '@mui/material';

export default class Team22Layout extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Hidden', false),
            $.observe('menu entries', true),
        ]);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [hidden, menuEntries] = props.values;

            const getIconComponent = (iconName) => {
                const icons = {
                    Heart,
                    Home,
                    Calendar,
                    CalendarRange,
                    TvMinimalPlay,
                    MessageCircleMore,
                    Users,
                    FileText,
                    Bell,
                    UserRoundCog,
                    LifeBuoy,
                    Earth,
                    HeartHandshake,
                    ThumbsUp,
                    Lock,
                    History
                };
                return icons[iconName] || Home;
            };

            const [menu, setMenu] = useState([]);
            const [isOpen, setIsOpen] = useState((localStorage.getItem('menu-is-open')?.toLowerCase() ?? 'true') === 'true');
            const [activeItem, setActiveItem] = useState(parseCurrentHash());
            const [isMobile, setIsMobile] = useState(false);
            const [boxEl, setBoxEl] = React.useState(null);

            const renderer = useProperty($, 'renderer', true);
            const hasNotifications = useProperty($, 'has notifications', true);
            const isMobileMenuOpen = useProperty($, 'is mobile menu open', true);

            useEffect(() => {
                setMenu(menuEntries?.toArray().map(a => a.toJSON()));
            }, [menuEntries]);

            useEffect(() => {
                const checkMobile = () => {
                    setIsMobile(window.innerWidth < 768);
                    if (window.innerWidth < 768) {
                        setIsOpen(false);
                    }
                };

                checkMobile();
                window.addEventListener('resize', checkMobile);
                return () => window.removeEventListener('resize', checkMobile);
            }, []);

            React.useEffect(() => {
                let renderer$;
                if (boxEl && !hidden && renderer) {
                    renderer$ = $.runner(renderer).setParentElement(boxEl);
                    const onChange = () => {
                        renderer$.set('Width', boxEl.getBoundingClientRect().width);
                        renderer$.set('Height', boxEl.getBoundingClientRect().height);
                    }
                    new ResizeObserver(onChange).observe(boxEl);
                }
                return () => {
                    renderer$?.destroy();
                };
            }, [boxEl, renderer, hidden]);

            const getSlang = () => {
                const fullId = CloudObject.get(Auth.getCurrentUser()).get(User.SAMLnameIdProp);
                if (fullId) {
                    let parts = fullId.split('.');
                    if (parts.length < 2) {
                        parts = fullId.split(' ');
                    }
                    if (parts.length < 2) {
                        parts = [fullId[0], fullId[1]]
                    }
                    return fullId ? parts.map(a => a[0].toUpperCase()).join('') : '';
                } else {
                    return '';
                }
            }

            const toggleMenu = () => {
                if (isMobile) {
                    $.set('is mobile menu open', !isMobileMenuOpen);
                } else {
                    const newIsOpen = !isOpen;
                    localStorage.setItem('menu-is-open', newIsOpen.toString());
                    setIsOpen(newIsOpen);
                }
            };

            const handleMenuClick = (item) => {
                const lambda = $.get('on menu click');
                const [ctrlFlow, menuEntry] = lambda.getInputs();
                const runner$ = $.runner(lambda)
                    .set(menuEntry, item)
                    .set(ctrlFlow, new Date());
                $.onDestroy(() => runner$.destroy());

                setActiveItem(item.path);
                if (isMobile) {
                    $.set('is mobile menu open', false);
                }
            };

            const MenuItems = ({ mobile = false }) => (
                <div className={`py-6 ${mobile ? 'px-6' : 'px-3'}`}>
                    {menu?.sort((a, b) => a.rank - b.rank).map((item, idx) => {
                        const Icon = getIconComponent(item.icon);
                        return (
                            <div
                                key={item.path}
                                className={`relative my-1 ${!mobile && isOpen ? 'px-2' : 'px-1'}`}
                            >
                                <button
                                    onClick={() => handleMenuClick(item)}
                                    className={`
                                        w-full flex items-center py-3 px-3 rounded-xl
                                        transition-all duration-200 group
                                        ${activeItem === item.path || (idx === 0 && (activeItem === '' || activeItem === '/'))
                                        ? 'bg-blue-500/10 text-blue-600'
                                        : 'hover:bg-blue-500/5 hover:text-blue-500 text-gray-500'}   
                                    `}
                                >
                                    <Icon
                                        size={20}
                                        className={`transition-transform group-hover:scale-110 ${
                                            activeItem === item.path || (idx === 0 && (activeItem === '' || activeItem === '/')) ? 'text-blue-500' : 'text-gray-400'
                                        }`}
                                    />
                                    {(mobile || isOpen) && (
                                        <span className="ml-3 truncate font-medium">
                                            {item.description}
                                        </span>
                                    )}
                                    {(activeItem === item.path || (idx === 0 && (activeItem === '' || activeItem === '/'))) && !mobile && (
                                        <div className="absolute left-0 w-1 h-8 bg-blue-500 rounded-r-full" />
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            );

            return !hidden && (
                <div className="flex flex-col h-screen bg-slate-50 text-gray-800">
                    {/* Header */}
                    <header className="h-16 bg-white/80 backdrop-blur-sm border-b border-slate-200 px-6 flex items-center justify-between z-20 shadow-sm">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={toggleMenu}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <Menu size={20} className="text-gray-600" />
                            </button>
                            <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-sky-500 bg-clip-text text-transparent">
                                {useProperty($, 'title', true)}
                            </h1>
                        </div>

                        <div className="flex items-center space-x-6">
                            <div className="relative hidden md:block">
                                <input
                                    type="text"
                                    placeholder={$.get('search label') ?? 'Search...'}
                                    className="bg-slate-100/50 w-64 px-4 py-2 rounded-lg border border-slate-200
                                             focus:border-blue-300 focus:ring-1 focus:ring-blue-200
                                             focus:outline-none transition-all placeholder-slate-400"
                                />
                                <Search size={18} className="absolute right-3 top-2.5 text-slate-400" />
                            </div>
                            <button className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors" onClick={() => $.trigger('on notifications click')}>
                                <Bell size={20} className="text-gray-600" />
                                {hasNotifications && (<span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full" />)}
                            </button>
                            <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-sky-500
                            rounded-full ring-2 ring-blue-200 ring-offset-2 flex items-center justify-center"  onClick={() => $.trigger('on account click')}>
                                <span className="font-bold text-white">{getSlang()}</span>
                            </div>
                        </div>
                    </header>

                    <div className="flex flex-1 overflow-hidden">
                        {/* Desktop Sidebar */}
                        {!isMobile && (
                            <div
                                className={`relative transition-all duration-300 ease-in-out ${
                                    isOpen ? 'w-64' : 'w-20'
                                } bg-gradient-to-b from-slate-50 to-white border-r border-slate-200`}
                            >
                                <MenuItems />
                            </div>
                        )}

                        {/* Mobile Menu Overlay */}
                        {isMobile && isMobileMenuOpen && (
                            <div className="fixed inset-0 bg-black/20 w-full backdrop-blur-sm z-30">
                                <div className="fixed inset-y-0 left-0 w-full bg-white shadow-lg z-40">
                                    <div className="flex items-center justify-between p-4 border-b border-slate-200">
                                        <h2 className="text-xl font-semibold text-blue-600">Menu</h2>
                                        <div>
                                            <button className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors" onClick={() => { $.set('is mobile menu open', false); $.trigger('on notifications click')}}>
                                                <Bell size={20} className="text-gray-600" />
                                                {hasNotifications && (<span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full" />)}
                                            </button>
                                            <button
                                                onClick={() => $.set('is mobile menu open', false)}
                                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                            >
                                                <X size={20} className="text-gray-600" />
                                            </button>
                                        </div>
                                    </div>
                                    <MenuItems mobile={true} />
                                </div>
                            </div>
                        )}

                        {/* Main Content */}
                        <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-100 via-white to-blue-50/30"
                             style={{position: 'relative'}}>
                            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100/40 via-transparent to-transparent"></div>
                            <Box
                                ref={setBoxEl}
                                sx={{
                                    width: 1,
                                    height: 1,
                                    boxSizing: 'border-box',
                                    position: 'absolute',
                                    overflow: useProperty($, 'Allow Content Overflow') ? 'visible' : 'hidden'
                                }}
                            />
                        </div>
                    </div>
                </div>
            );
        }
    }
}

registerBrick('019574be160459c55d0e', Team22Layout);